import * as React from 'react';

import { DropDown, SvgButton } from '@coursera/coursera-ui';
import { SvgMoreHorizontalFilled } from '@coursera/coursera-ui/svg';

import _t from 'i18n!nls/staff-management';

import 'css!./__styles__/DropdownMenu';

type Props = {
  /** Dropdown menu items. */
  children?(args: { hide(): void }): React.ReactNode;
  /** Override the default button icon if desired. */
  buttonSvgElement?: React.ReactElement;
  /** Follows rules for the dropDownPosition of the DropDownMenu. */
  dropDownPosition?: {
    [x: string]: string;
  };
};

/** Managed state of DropDown.Menu. */
type State = {
  anchorElement?: HTMLElement;
  isOpen?: boolean;
};

/**
 * A common dropdown menu for internal use in the staff management module where we intend
 * the following:
 *
 * - All "instances" of this behave as a singleton, as in there can be only one... visible on
 *   the screen at a time (opening "instance B" will close "instance A").
 * - The menu action will be displayed as an icon button, and the positioning of the menu will
 *   be anchored to the button.
 */
export class DropdownMenu extends React.Component<Props, State> {
  state = {
    isOpen: false,
  };

  static defaultProps = {
    // Designs may eventually dictate a different title, the general usage of this will
    // apply an action to a staff member.
    get buttonSvgElement() {
      return <SvgMoreHorizontalFilled title={_t('Staff Actions')} size={30} data-e2e="btn-menu" />;
    },
    dropDownPosition: { vertical: 'bottom', horizontal: 'right' },
  };

  show = (ev: React.SyntheticEvent<HTMLElement>) => {
    this.setState({
      anchorElement: ev.currentTarget,
      isOpen: true,
    });
  };

  hide = () => {
    this.setState({ isOpen: false });
  };

  static e2eName = 'staff-management-dropdown-menu';

  render() {
    const { buttonSvgElement, children, dropDownPosition } = this.props;

    return (
      <div className="rc-DropdownMenu" data-testid={DropdownMenu.e2eName}>
        <SvgButton
          onClick={this.show}
          type="icon"
          svgElement={buttonSvgElement}
          rootClassName="drop-down-action-button"
        />
        <DropDown.Menu {...this.state} dropDownPosition={dropDownPosition} onRequestClose={this.hide}>
          {children?.({ hide: this.hide })}
        </DropDown.Menu>
      </div>
    );
  }
}

export default DropdownMenu;
